import {
  ArrowRight,
  ClockCounterClockwise,
  Copy,
  DotsThreeVertical,
  PauseCircle,
  PencilSimpleLine,
  PlayCircle,
  Trash,
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

import RenderIf from "common/components/RenderIf";
import RestrictedComponent from "common/components/RestrictedComponent";
import { Button } from "common/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import type { Campaign } from "common/types";

import useCampaignValidation from "../../hooks/useCampaignValidation";

import type { CampaignActionType, DetailedCampaign } from "../../types";

interface CampaignActionsDropdownProps {
  campaign: Campaign | DetailedCampaign;
  isDetailedView?: boolean;
  setAction: (action: CampaignActionType) => void;
}

export default function CampaignActionsDropdown({
  campaign,
  isDetailedView = false,
  setAction,
}: CampaignActionsDropdownProps) {
  const navigate = useNavigate();
  const { state, id, owners } = campaign;
  const { accounts_count } = useSelectedWorkspaceContext();
  const { hasInsufficientLicenses } = useCampaignValidation(owners);

  const isDuplicateDisabled = accounts_count === 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          intent="iconOnly"
          variant="secondary-black"
          size="sm"
          onClick={(e) => e.stopPropagation()}
        >
          <DotsThreeVertical />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuList>
          {isDetailedView ? (
            <RestrictedComponent visibleForPermissions={["campaigns_full"]}>
              {(isDisabled) => (
                <DropdownMenuItem
                  onClick={() => {
                    setAction("editName");
                  }}
                  disabled={isDisabled}
                >
                  <PencilSimpleLine />
                  Edit name
                </DropdownMenuItem>
              )}
            </RestrictedComponent>
          ) : (
            <DropdownMenuItem onClick={() => navigate(id)}>
              <ArrowRight />
              Go to details
            </DropdownMenuItem>
          )}
          <RestrictedComponent visibleForPermissions={["campaigns_full"]}>
            <DropdownMenuItem
              onClick={() => setAction("duplicate")}
              disabled={isDuplicateDisabled}
            >
              <Copy />
              Duplicate campaign
            </DropdownMenuItem>
            <RenderIf
              condition={
                state === "CREATED" ||
                state === "PAUSED" ||
                state === "SCHEDULED"
              }
            >
              <DropdownMenuItem
                onClick={() =>
                  setAction(hasInsufficientLicenses ? "license" : "start")
                }
              >
                <PlayCircle />
                Start campaign
              </DropdownMenuItem>
            </RenderIf>
            <RenderIf condition={state === "ACTIVE"}>
              <DropdownMenuItem
                onClick={() => {
                  setAction("pause");
                }}
              >
                <PauseCircle />
                Pause campaign
              </DropdownMenuItem>
            </RenderIf>
            <RenderIf condition={state === "CREATED"}>
              <DropdownMenuItem
                onClick={() =>
                  setAction(hasInsufficientLicenses ? "license" : "schedule")
                }
              >
                <ClockCounterClockwise />
                Schedule campaign
              </DropdownMenuItem>
            </RenderIf>
            <RenderIf condition={state === "SCHEDULED"}>
              <DropdownMenuItem
                onClick={() => {
                  setAction("unschedule");
                }}
              >
                <ClockCounterClockwise />
                Remove schedule
              </DropdownMenuItem>
            </RenderIf>
            <DropdownMenuItem
              variant="danger"
              onClick={() => {
                setAction("delete");
              }}
            >
              <Trash />
              Delete campaign
            </DropdownMenuItem>
          </RestrictedComponent>
        </DropdownMenuList>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
